import React, { useCallback, useState } from 'react'
import AccountAccessForgetPassword, { FORGOT_PASSWORD_TITLE } from './AccountAccessForgetPassword'
import AccountAccessModalWrapper from './AccountAccessModalWrapper'
import AccountAccessJoin from './AccountAccessJoin'
import AccountAccessLoginPassword from './AccountAccessLoginPassword'
import AccountAccessPartnershipLinkAccount from './AccountAccessPartnershipLinkAccount'
import AccountAccessResetPassword from './AccountAccessResetPassword'
import config from 'constants/config'
import BusinessTravellerLoginForm from 'businessTraveller/components/business-account/BusinessTravellerLoginForm'
import BusinessTravellerLoginV2Form from 'businessTraveller/components/business-account/BusinessTravellerLoginV2Form'
import useIsLoginV2Enabled from 'businessTraveller/hooks/useIsLoginV2Enabled'
import BusinessTravellerPasswordLoginForm from 'businessTraveller/components/business-account/BusinessTravellerPasswordLoginForm'
import AgentHubResendActivationEmail from 'agentHub/components/AgentHubAccountAccess/AgentHubResendActivationEmail'
import AccountAccessLoginOTP from './AccountAccessLoginOTP'
import AccountAccessAddPhone from './AccountAccessAddPhone'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import AccountAccessJoinExistingAccount from './AccountAccessJoinExistingAccount'
import { clearAuthError } from 'actions/AuthActions'
import AccountAccessAppleRandomEmail from './AccountAccessAppleRandomEmail'

interface Props {
  mode?: App.UiAccountModalMode;
  dismissable?: boolean;
  defaultEmail?: string;
  onModeChange?: (nextMode: App.UiAccountModalMode) => void;
  resetPasswordToken?: string;
  hotelOfferDetails?: App.AccountModalOfferDetails;
  callbackPath?: string;
  defaultAppleData?: App.AppleLoginData;
}

function AccountAccessModalContent(props: Props) {
  const {
    mode,
    defaultEmail,
    onModeChange,
    resetPasswordToken,
    callbackPath,
    dismissable,
    defaultAppleData,
  } = props

  const [userId, setUserId] = useState<string>(defaultEmail ?? '')
  const [applePrivateEmailData, setApplePrivateEmailData] = useState<App.AppleLoginData | null>(defaultAppleData ?? null)
  const isAgentHubMode = config.agentHub.isEnabled
  const user = useAppSelector(state => state.auth.users[userId])
  const dispatch = useAppDispatch()
  const isLoginV2Enabled = useIsLoginV2Enabled()

  const onChangeMode = useCallback((nextMode: App.UiAccountModalMode, nextUserId?: string) => {
    // errors are relevant only for the life of that 'mode', so reset them as we change
    dispatch(clearAuthError())
    onModeChange?.(nextMode)

    if (nextUserId !== undefined) {
      setUserId(nextUserId)
    }
  }, [dispatch, onModeChange])

  switch (mode) {
    case 'login':
    case 'requestAgentCallbackLogin':
    case 'tripPlannerLogin':
    case 'toursCatalogueLogin':
    case 'loyaltyLogin':
      return <AccountAccessModalWrapper dismissible={dismissable}>
        {isLoginV2Enabled && <BusinessTravellerLoginV2Form />}
        {!isLoginV2Enabled && <BusinessTravellerLoginForm onSubmit={setUserId} onModeChange={onChangeMode} />}
      </AccountAccessModalWrapper>
    case 'loginPassword':
      if (user) {
        return <AccountAccessModalWrapper onBackButtonClick={() => onChangeMode('login')}>
          <BusinessTravellerPasswordLoginForm user={user} onModeChange={onChangeMode} />
        </AccountAccessModalWrapper>
      }
      return null
    case 'loginPasswordOTP':
      if (user) {
        return <AccountAccessLoginPassword
          otpEnabled
          user={user}
          onModeChange={onChangeMode}
          dismissable={dismissable}
        />
      }
      return null
    case 'loginOTP':
      if (user) {
        return <AccountAccessLoginOTP user={user} onModeChange={onChangeMode} dismissable={dismissable} />
      }
      return null
    case 'promptAddPhone':
      return <AccountAccessAddPhone dismissable={dismissable} />
    case 'join':
      if (user) {
        return <AccountAccessJoin
          user={user}
          onModeChange={onChangeMode}
          dismissable={dismissable}
        />
      }
      return null
    case 'joinExistingAccount':
      if (user) {
        return <AccountAccessJoinExistingAccount user={user} onModeChange={onChangeMode} dismissable={dismissable} />
      }
      return null
    case 'forgotPassword':
      if (user) {
        return <AccountAccessModalWrapper
          title={FORGOT_PASSWORD_TITLE}
          onBackButtonClick={() => onChangeMode('login')}
          dismissible={dismissable}
        >
          <AccountAccessForgetPassword user={user} onModeChange={onChangeMode} />
        </AccountAccessModalWrapper>
      }
      return null
    case 'resetPassword':
      return <AccountAccessResetPassword token={resetPasswordToken!} callbackPath={callbackPath} dismissable={dismissable} />
    case 'linkPartnership':
      return <AccountAccessPartnershipLinkAccount dismissable={dismissable} />
    case 'agentResendActivation':
      if (isAgentHubMode && user?.email) {
        return <AgentHubResendActivationEmail requestedEmail={user.email} onModeChange={onChangeMode} />
      }
      return null
    case 'applePrivateEmail':
      if (user) {
        return <AccountAccessAppleRandomEmail
          user={user}
          onModeChange={onChangeMode}
          applePrivateEmailData={applePrivateEmailData}
          setApplePrivateEmailData={setApplePrivateEmailData}
        />
      }
      return null
    default:
      return null
  }
}

export default AccountAccessModalContent
